import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useProducts } from './useProducts';
import ImageModal from './ImageModal';
import VideoModal from './VideoModal';
import ProductPrice from './catalog/ProductPrice';
import ProductRefId from './catalog/ProductRefId';
import {
  productHasVideo,
  primaryVideoSource,
  videoPosterSrc,
  hrefForSameSiteAsset,
  productGalleryImagePaths,
} from './productMediaUtils';
import { formatDisplayTitle, formatProductTitle } from './formatProductTitle';
import AddToCartButton from './cart/AddToCartButton';
import ProductLetterRibbon from './cart/ProductLetterRibbon';
import WishlistHeartButton from './wishlist/WishlistHeartButton';
import CatalogPagination from './catalog/CatalogPagination';
import { productDetailPath } from './productPaths';

/**
 * Product grid for /shop/:categorySlug/:groupKey (not rakhi/lumba — those use RakhiList/LumbaList).
 * groupKey "all" shows every product in the category; otherwise matches type OR subcategory.
 */
const DynamicCategoryList = () => {
  const navigate = useNavigate();
  const { categorySlug, groupKey } = useParams();
  const decoded = groupKey ? decodeURIComponent(groupKey) : '';
  const { allProducts, categories, loading } = useProducts();
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const [imageModal, setImageModal] = useState({ show: false, image: '', alt: '' });
  const [videoModal, setVideoModal] = useState({ show: false, video: '', alt: '', noSound: true });
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [currentProduct, setCurrentProduct] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug, groupKey]);

  useEffect(() => {
    setPage(1);
  }, [categorySlug, groupKey]);

  const meta = categories.find((c) => c.slug === categorySlug);
  const titleBase = meta?.name || categorySlug;

  const productsToShow = allProducts.filter((p) => {
    if (p.category !== categorySlug) return false;
    if (!decoded || decoded === 'all') return true;
    return p.type === decoded || p.subcategory === decoded;
  });

  const heading =
    decoded && decoded !== 'all'
      ? `${titleBase} — ${decoded.replace(/-/g, ' ')}`
      : titleBase;

  const totalPages = Math.ceil(productsToShow.length / pageSize) || 1;
  const paginated = productsToShow.slice((page - 1) * pageSize, page * pageSize);

  const getCurrentImage = (product) => {
    const paths = productGalleryImagePaths(product);
    if (paths.length === 0) return '';
    const index = currentImageIndex[product.id] || 0;
    return paths[Math.min(Math.max(0, index), paths.length - 1)] || paths[0];
  };

  const handleImageNavigation = (product, direction) => {
    if (!product) return;
    const paths = productGalleryImagePaths(product);
    if (paths.length <= 1) return;
    const currentIndex = currentImageIndex[product.id] || 0;
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % paths.length;
    } else {
      newIndex = currentIndex === 0 ? paths.length - 1 : currentIndex - 1;
    }
    setCurrentImageIndex({ ...currentImageIndex, [product.id]: newIndex });
    if (imageModal.show && currentProduct?.id === product.id) {
      setImageModal({
        ...imageModal,
        image: paths[newIndex],
        alt: formatProductTitle(product.name),
      });
    }
  };

  const openProductDetail = (product, e) => {
    if (e?.target?.closest?.('.image-navigation, .nav-btn, .product-card__wishlist, .add-to-cart-block')) {
      return;
    }
    const to = productDetailPath(product);
    if (to && to !== '/shop') navigate(to);
  };

  const handleMediaZoom = (product, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setCurrentProduct(product);
    const isVideo = productHasVideo(product);
    const src = primaryVideoSource(product);
    if (isVideo && src) {
      setVideoModal({
        show: true,
        video: src,
        alt: formatProductTitle(product.name),
        noSound: product.noSound !== false,
      });
    } else {
      const paths = productGalleryImagePaths(product);
      if (paths.length === 0) return;
      const index = Math.min(Math.max(0, currentImageIndex[product.id] || 0), paths.length - 1);
      setImageModal({ show: true, image: paths[index], alt: formatProductTitle(product.name) });
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center text-muted">Loading…</div>
    );
  }

  return (
    <div className="RakhiList-main product-list-page">
      <ImageModal
        show={imageModal.show}
        image={imageModal.image}
        alt={imageModal.alt}
        onClose={() => {
          setImageModal({ show: false, image: '', alt: '' });
          setCurrentProduct(null);
        }}
        onNext={() => currentProduct && handleImageNavigation(currentProduct, 'next')}
        onPrev={() => currentProduct && handleImageNavigation(currentProduct, 'prev')}
        hasMultipleImages={currentProduct ? productGalleryImagePaths(currentProduct).length > 1 : false}
        currentIndex={currentProduct ? currentImageIndex[currentProduct.id] || 0 : 0}
        totalImages={currentProduct ? productGalleryImagePaths(currentProduct).length : 1}
      />
      <VideoModal
        show={videoModal.show}
        video={videoModal.video}
        alt={videoModal.alt}
        noSound={videoModal.noSound}
        onClose={() => setVideoModal({ show: false, video: '', alt: '', noSound: true })}
      />

      <div className="container store-page__container mb-3">
        <nav aria-label="breadcrumb" className="small">
          <Link to="/shop">Shop</Link> /{' '}
          <Link to={`/shop/${categorySlug}`}>{formatDisplayTitle(titleBase)}</Link>
          {decoded && decoded !== 'all'
            ? ` / ${formatDisplayTitle(decoded.replace(/-/g, ' '))}`
            : ''}
        </nav>
      </div>

      <h2 className="page-heading-script mb-4 text-center">
        {formatProductTitle(heading)}
      </h2>

      {paginated.length === 0 ? (
        <div className="text-center py-5">
          <p>No products found.</p>
          <Link to={`/shop/${categorySlug}`} className="btn btn-outline-primary">
            Back to {formatDisplayTitle(titleBase)}
          </Link>
        </div>
      ) : (
        <div className="product-grid">
          {paginated.map((product) => {
            const isVideo = productHasVideo(product);
            const videoSrc = primaryVideoSource(product);
            const galleryPaths = productGalleryImagePaths(product);
            const hasMultipleImages = galleryPaths.length > 1;
            const currentIndex = currentImageIndex[product.id] || 0;
            const totalImages = galleryPaths.length || 1;

            return (
              <div key={product.id} className="product-card">
                <div
                  className="product-media-container product-media-container--link"
                  onClick={(e) => openProductDetail(product, e)}
                  onDoubleClick={(e) => handleMediaZoom(product, e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openProductDetail(product, e);
                  }}
                  role="link"
                  tabIndex={0}
                  aria-label={`View ${formatProductTitle(product.name)}`}
                >
                  <ProductLetterRibbon product={product} />
                  <WishlistHeartButton
                    product={product}
                    categorySlug={categorySlug}
                    size="sm"
                    className="product-card__wishlist"
                  />
                  {isVideo && videoSrc ? (
                    <div className="video-thumbnail">
                      <img src={videoPosterSrc(product)} alt={formatProductTitle(product.name)} />
                      <div className="play-icon">▶</div>
                    </div>
                  ) : (
                    <div className="image-container">
                      <img
                        src={hrefForSameSiteAsset(getCurrentImage(product) || '')}
                        alt={formatProductTitle(product.name)}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: 'auto',
                          aspectRatio: '1',
                          objectFit: 'cover',
                        }}
                      />
                      {hasMultipleImages && (
                        <div
                          className="image-navigation"
                          onClick={(e) => e.stopPropagation()}
                          role="presentation"
                        >
                          <button
                            type="button"
                            onClick={() => handleImageNavigation(product, 'prev')}
                            className="nav-btn prev"
                            aria-label="Previous image"
                          >
                            ❮
                          </button>
                          <div className="image-counter">
                            {currentIndex + 1}/{totalImages}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleImageNavigation(product, 'next')}
                            className="nav-btn next"
                            aria-label="Next image"
                          >
                            ❯
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="product-info">
                  <h3 className="product-title">
                    <Link to={productDetailPath(product)} className="text-decoration-none text-body">
                      {formatProductTitle(product.name)}
                    </Link>
                  </h3>
                  <ProductRefId id={product.id} />
                  <ProductPrice product={product} className="product-price" showBadge={false} />
                  <AddToCartButton
                    product={product}
                    categorySlug={categorySlug}
                    className="mt-2"
                    compact
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <CatalogPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}
    </div>
  );
};

export default DynamicCategoryList;
